import { useCallback, useState } from 'react';
import { lsLoad } from '../../tools/Storage';
import StorageEnum from '../../enums/Storage.enum';
import type {
  MarketMarketIO,
  UserMarketsUserMarketsIO,
  UserProfileProfileOutput,
  UserUserDeactivateActivateInput,
  UserUserIO,
} from '../../generated/fresh-guard-api';
import dayjs from 'dayjs';
import { UserManagementContext } from './UserManagement.context.tsx';
import type UserManagementProperties from './UserManagement.properties.ts';
import { useTranslation } from 'react-i18next';

export const UserManagementProvider = (props: UserManagementProperties) => {
  const [users, setUsers] = useState<UserUserIO[]>([]);
  const { t } = useTranslation();

  const updateUserState = useCallback((users: UserUserIO[]) => {
    setUsers((prev) => {
      const indexByUsername = new Map(prev.map((u, i) => [u.username, i]));
      const next = [...prev];
      const newOnes: typeof prev = [];

      for (const incoming of users) {
        const idx = indexByUsername.get(incoming.username);
        if (idx !== undefined) {
          next[idx] = incoming;
        } else {
          newOnes.push(incoming);
        }
      }

      return [...next, ...newOnes];
    });
  }, []);

  const updateDeactivatedOrActivatedUsers = useCallback(
    (values: UserUserDeactivateActivateInput | null) => {
      if (!values) return;

      const toActivate = new Set(values.usersForActivation ?? []);
      const toDeactivate = new Set(values.usersForDeactivation ?? []);
      const now = dayjs().format(t('date.apiFormat'));
      const currentUser = (lsLoad(StorageEnum.UserProfile) as UserProfileProfileOutput).username;

      setUsers((prev) =>
        prev.map((u) => {
          if (u.username && toActivate.has(u.username)) {
            u.deactivated = null;
            u.deactivatedBy = null;

            return u;
          }
          if (u.username && toDeactivate.has(u.username)) {
            u.deactivated = now;
            u.deactivatedBy = currentUser;

            return u;
          }
          return u;
        })
      );
    },
    [t]
  );

  const updateMarkets = useCallback(
    (markets: MarketMarketIO[], userMarketsUserMarketsIO: UserMarketsUserMarketsIO | undefined) => {
      if (userMarketsUserMarketsIO) {
        const user = users.find((user) => user.username === userMarketsUserMarketsIO.username);

        if (user) {
          const marketIds = userMarketsUserMarketsIO.markets?.map((m) => m.id);
          const selectedMarkets = markets.filter(
            (market) => market.id && marketIds?.includes(market.id)
          );
          user.markets = selectedMarkets.map((selected) => selected.name).join(', ');
          updateUserState([user]);
        }
      }
    },
    [updateUserState, users]
  );

  return (
    <UserManagementContext.Provider
      value={{
        users,
        setUsers,
        updateUserState,
        updateDeactivatedOrActivatedUsers,
        updateMarkets,
      }}
    >
      {props.children}
    </UserManagementContext.Provider>
  );
};
